import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router-dom";

const LOCAL_STORAGE_KEY = "hdi_inspection_session";
const INPUT_MODE_STORAGE_KEY = "hdi_inspection_input_mode";
const DIRECT_CODE_LIMIT = 100;

const COMPONENT_BATCH_OPTIONS = [
  "Elfab December 2024",
  "Elfab January 2025",
  "Elfab June 2025",
  "Elfab December 2025",
  "Elfab February 2026",
];

const SCAN_REQUEST_TIMEOUT_MS = Number.parseInt(
  window.APP_CONFIG?.REACT_APP_SCAN_REQUEST_TIMEOUT_MS ||
    process.env.REACT_APP_SCAN_REQUEST_TIMEOUT_MS ||
    "180000",
  10
);

const EMPTY_HDI_BOX = {
  source: "image",
  segment_index: 0,
  row: 0,
  column: 0,
  bbox: null,
  detected: false,
  placeholder: true,
  enabled: false,
  name_label: "",
  data_matrix: "",
  location: "",
  company: "",
  order_number: "",
  panel_number: "",
  pcb_number: "",
  detector: "",
  pcb: "",
  version: "",
  serial_number: "",
  component_mounting_batch: "",
  production_date: "",
  inspection_date: "",
  optical_inspection: "",
  comments: "",
};

const REQUIRED_EXPORT_FIELDS = [
  ["name_label", "Name Label"],
  ["data_matrix", "Data Matrix Code"],
  ["company", "Company"],
  ["order_number", "Order Number"],
  ["panel_number", "Panel Number"],
  ["pcb_number", "PCB Number"],
  ["detector", "Detector"],
  ["pcb", "PCB"],
  ["version", "Version"],
  ["location", "Location"],
  ["component_mounting_batch", "Component Mounting Batch"],
  ["optical_inspection", "Optical Inspection"],
  ["inspection_date", "Inspection Date"],
];

const IDENTIFICATION_FIELDS = [
  ["name_label", "Name Label"],
  ["data_matrix", "Data Matrix Code"],
  ["serial_number", "Serial Number"],
  ["company", "Company"],
  ["detector", "Detector"],
  ["pcb", "PCB"],
  ["order_number", "Order Number"],
  ["panel_number", "Panel Number"],
  ["pcb_number", "PCB Number"],
  ["version", "Version"],
];

function createHdiBox(overrides = {}) {
  return {
    ...EMPTY_HDI_BOX,
    ...overrides,
  };
}

function normalizeHdiBox(box, index = 0) {
  const source = box?.source === "direct" ? "direct" : "image";
  const normalized = createHdiBox({
    ...box,
    source,
    segment_index: Number.isInteger(box?.segment_index) ? box.segment_index : index,
    row: source === "direct" ? null : Number.isInteger(box?.row) ? box.row : 0,
    column: source === "direct" ? null : Number.isInteger(box?.column) ? box.column : 0,
  });

  normalized.detected = Boolean(box?.detected ?? normalized.data_matrix);
  normalized.placeholder =
    typeof box?.placeholder === "boolean" ? box.placeholder : !normalized.detected;
  normalized.enabled =
    typeof box?.enabled === "boolean" ? box.enabled : Boolean(normalized.detected);
  if (normalized.optical_inspection === "Untested") {
    normalized.optical_inspection = "";
  }

  return normalized;
}

function migrateLegacyDetectedHdis(detectedHdis = []) {
  return detectedHdis.map((hdi, index) =>
    normalizeHdiBox(
      {
        ...hdi,
        segment_index: index,
        row: index,
        column: 0,
        detected: true,
        placeholder: false,
        enabled: true,
      },
      index
    )
  );
}

function applyMetadataDefaults(box, defaults) {
  const productionDateOverride = defaults.productionDateOverride || "";
  return {
    ...box,
    location: defaults.location,
    component_mounting_batch: defaults.componentBatch,
    optical_inspection: defaults.opticalInspection,
    production_date: productionDateOverride || box.production_date || "",
    production_date_source: productionDateOverride ? "" : box.production_date_source || "",
    inspection_date: defaults.inspectionDate,
    comments: defaults.comment,
  };
}

function getCommonMappedProductionDate(hdiGrid) {
  const detectedHdis = hdiGrid.filter((box) => box.detected);
  if (detectedHdis.length === 0) {
    return "";
  }

  const mappedDates = detectedHdis.map((box) =>
    box.production_date_source === "code_rule" ? box.production_date : ""
  );
  if (mappedDates.some((date) => !date)) {
    return "";
  }

  const uniqueDates = [...new Set(mappedDates)];
  return uniqueDates.length === 1 ? uniqueDates[0] : "";
}

function getGridColumns(hdiGrid) {
  if (hdiGrid.length === 0) {
    return 1;
  }

  return Math.max(...hdiGrid.map((box) => box.column + 1), 1);
}

function getGridRows(hdiGrid) {
  if (hdiGrid.length === 0) {
    return 0;
  }

  return Math.max(...hdiGrid.map((box) => box.row + 1), 1);
}

function serializeHdiBox(box) {
  const panelNumber = (box.panel_number || "").trim();
  const pcbNumber = (box.pcb_number || "").trim();
  const serialNumber =
    (box.serial_number || "").trim() ||
    (panelNumber && pcbNumber ? `${panelNumber}-${pcbNumber}` : "");

  return {
    name_label: (box.name_label || "").trim(),
    data_matrix: (box.data_matrix || "").trim(),
    location: (box.location || "").trim(),
    company: (box.company || "").trim(),
    order_number: (box.order_number || "").trim(),
    panel_number: panelNumber,
    pcb_number: pcbNumber,
    detector: (box.detector || "").trim(),
    pcb: (box.pcb || "").trim(),
    version: (box.version || "").trim(),
    serial_number: serialNumber,
    component_mounting_batch: (box.component_mounting_batch || "").trim(),
    production_date: box.production_date || "",
    inspection_date: box.inspection_date || "",
    optical_inspection: (box.optical_inspection || "").trim(),
    comments: box.comments || "",
  };
}

function getEnabledExportHdis(hdiGrid) {
  return hdiGrid.filter((box) => box.enabled).map(serializeHdiBox);
}

function getBoxTitle(box, index) {
  if (box.name_label) {
    return box.name_label;
  }

  return `Segment ${index + 1}`;
}

export default function HdiInspection() {
  const auth = useAuth();
  const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const [scanImage, setScanImage] = useState(null);
  const [hdiGrid, setHdiGrid] = useState([]);
  const [scanStatus, setScanStatus] = useState("");
  const [scanError, setScanError] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [acquisitionMode, setAcquisitionMode] = useState(() => {
    try {
      return localStorage.getItem(INPUT_MODE_STORAGE_KEY) || "direct";
    } catch (_err) {
      return "direct";
    }
  });
  const [directInput, setDirectInput] = useState("");
  const [bulkInput, setBulkInput] = useState("");
  const [directFeedback, setDirectFeedback] = useState([]);
  const scanInputRef = useRef(null);
  const directQueueRef = useRef(Promise.resolve());
  const feedbackIdRef = useRef(0);
  const hdiGridRef = useRef([]);
  const metadataDefaultsRef = useRef({});
  const acquisitionGenerationRef = useRef(0);

  const [location, setLocation] = useState("PSI");
  const [componentBatch, setComponentBatch] = useState("");
  const [opticalInspection, setOpticalInspection] = useState("");
  const [productionDate, setProductionDate] = useState("");
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split("T")[0]);
  const [comment, setComment] = useState("");

  const [downloadError, setDownloadError] = useState("");
  const [inspectionDateFromExif, setInspectionDateFromExif] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!saved) {
        return;
      }

      const data = JSON.parse(saved);
      const restoredGrid = Array.isArray(data.hdiGrid)
        ? data.hdiGrid.map((box, index) => normalizeHdiBox(box, index))
        : migrateLegacyDetectedHdis(data.detectedHdis || []);
      const restoredMode =
        data.acquisitionMode === "direct" || data.acquisitionMode === "image"
          ? data.acquisitionMode
          : restoredGrid.length > 0
            ? "image"
            : localStorage.getItem(INPUT_MODE_STORAGE_KEY) || "direct";

      setHdiGrid(restoredGrid);
      hdiGridRef.current = restoredGrid;
      setAcquisitionMode(restoredMode);
      setLocation(data.location || "PSI");
      setComponentBatch(data.componentBatch || "");
      setOpticalInspection(
        data.opticalInspection === "Untested" ? "" : data.opticalInspection || ""
      );
      setProductionDate(data.productionDate || "");
      setInspectionDate(data.inspectionDate || new Date().toISOString().split("T")[0]);
      setComment(data.comment || "");
      setInspectionDateFromExif(Boolean(data.inspectionDateFromExif));

      if (restoredGrid.length > 0) {
        setScanStatus(`Session restored: ${restoredGrid.length} HDI item(s) loaded`);
      }
    } catch (err) {
      console.error("Error loading from localStorage:", err);
    }
  }, []);

  useEffect(() => {
    try {
      const sessionData = {
        hdiGrid,
        acquisitionMode,
        location,
        componentBatch,
        opticalInspection,
        productionDate,
        inspectionDate,
        comment,
        inspectionDateFromExif,
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sessionData));
    } catch (err) {
      console.error("Error saving to localStorage:", err);
    }
  }, [
    hdiGrid,
    location,
    componentBatch,
    opticalInspection,
    productionDate,
    inspectionDate,
    comment,
    inspectionDateFromExif,
    acquisitionMode,
  ]);

  useEffect(() => {
    hdiGridRef.current = hdiGrid;
  }, [hdiGrid]);

  useEffect(() => {
    metadataDefaultsRef.current = {
      location,
      componentBatch,
      opticalInspection,
      productionDateOverride: productionDate,
      inspectionDate,
      comment,
    };
  }, [location, componentBatch, opticalInspection, productionDate, inspectionDate, comment]);

  useEffect(() => {
    try {
      localStorage.setItem(INPUT_MODE_STORAGE_KEY, acquisitionMode);
    } catch (err) {
      console.error("Error saving HDI input preference:", err);
    }
  }, [acquisitionMode]);

  useEffect(() => {
    if (acquisitionMode === "direct" && auth.isAuthenticated) {
      scanInputRef.current?.focus();
    }
  }, [acquisitionMode, auth.isAuthenticated]);

  const opticalInspectionOptions = [
    "Good",
    "Bad",
    "Needs Expert Inspection",
    "Needs Repair",
    "Untested",
  ];

  const locations = [
    "PSI",
    "Hamburg",
    "CERN",
    "Santander",
    "Torino",
    "Vilnius",
    "Washington",
    "Chicago",
    "Cornell",
    "Firenze",
    "Helsinki",
    "Rice",
    "Purdue",
    "Zurich ETH",
    "Zurich UZH",
  ];

  const enabledExportHdis = getEnabledExportHdis(hdiGrid);
  const isDirectMode = acquisitionMode === "direct";
  const gridColumns = isDirectMode ? 1 : getGridColumns(hdiGrid);
  const gridRows = isDirectMode ? hdiGrid.length : getGridRows(hdiGrid);
  const detectedCount = hdiGrid.filter((box) => box.detected).length;
  const enabledCount = hdiGrid.filter((box) => box.enabled).length;
  const detectorValues = hdiGrid.map((box) => box.detector).filter(Boolean);
  const allTepx = detectorValues.length > 0 && detectorValues.every((value) => value === "TEPX");
  const commonMappedProductionDate = getCommonMappedProductionDate(hdiGrid);
  const effectiveProductionDate = productionDate || commonMappedProductionDate;
  const hasMappedProductionDate = hdiGrid.some((box) => box.production_date_source === "code_rule");

  const clearAcquisitionResults = () => {
    acquisitionGenerationRef.current += 1;
    directQueueRef.current = Promise.resolve();
    hdiGridRef.current = [];
    setHdiGrid([]);
    setScanImage(null);
    setDirectInput("");
    setBulkInput("");
    setDirectFeedback([]);
    setScanStatus("");
    setScanError("");
    setDownloadError("");
    setInspectionDateFromExif(false);
  };

  const switchAcquisitionMode = (nextMode) => {
    if (nextMode === acquisitionMode) return;
    if (
      hdiGridRef.current.length > 0 &&
      !window.confirm(
        "Switching input mode will remove the current HDI acquisition results. Inspection metadata will be preserved."
      )
    ) {
      return;
    }
    clearAcquisitionResults();
    setAcquisitionMode(nextMode);
  };

  const updateFeedbackItems = (updates) => {
    setDirectFeedback((current) =>
      current.map((item) => (updates[item.id] ? { ...item, ...updates[item.id] } : item))
    );
  };

  const processDirectBatch = async (queuedItems) => {
    try {
      const res = await fetch(`${apiUrl}/api/v1/hdi/parse-codes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ codes: queuedItems.map((item) => item.rawCode) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || `Code validation failed (${res.status}).`);
      }
      if (queuedItems[0]?.generation !== acquisitionGenerationRef.current) {
        return;
      }

      const results = Array.isArray(data.results) ? data.results : [];
      let nextGrid = [...hdiGridRef.current];
      let nextSegmentIndex =
        Math.max(-1, ...nextGrid.map((box) => Number(box.segment_index) || 0)) + 1;
      let acceptedCount = 0;
      const updates = {};

      queuedItems.forEach((item, itemIndex) => {
        const result = results.find((candidate) => candidate.index === itemIndex);
        if (!result) {
          updates[item.id] = {
            status: "network",
            message: "The server did not return a result for this code.",
          };
          return;
        }

        if (result.status !== "accepted" || !result.hdi) {
          updates[item.id] = {
            code: result.normalized_code || item.rawCode,
            status: result.error?.code === "duplicate" ? "duplicate" : "invalid",
            message: result.error?.message || "The code is not valid.",
          };
          return;
        }

        const normalizedCode = result.normalized_code;
        const existingIndex = nextGrid.findIndex(
          (box) => (box.data_matrix || "").trim() === normalizedCode
        );
        if (existingIndex >= 0) {
          updates[item.id] = {
            code: normalizedCode,
            status: "duplicate",
            message: `Already scanned as item ${existingIndex + 1}: ${getBoxTitle(
              nextGrid[existingIndex],
              existingIndex
            )}.`,
          };
          return;
        }

        const nextBox = applyMetadataDefaults(
          normalizeHdiBox(
            {
              ...result.hdi,
              source: "direct",
              segment_index: nextSegmentIndex,
              row: null,
              column: null,
              bbox: null,
              detected: true,
              placeholder: false,
              enabled: true,
            },
            nextSegmentIndex
          ),
          metadataDefaultsRef.current
        );
        nextGrid.push(nextBox);
        nextSegmentIndex += 1;
        acceptedCount += 1;
        updates[item.id] = {
          code: normalizedCode,
          status: "accepted",
          message: `Added ${nextBox.name_label}.`,
        };
      });

      hdiGridRef.current = nextGrid;
      setHdiGrid(nextGrid);
      updateFeedbackItems(updates);
      setScanStatus(
        acceptedCount > 0
          ? `Added ${acceptedCount} HDI${acceptedCount === 1 ? "" : "s"}. Ready for the next scan.`
          : "No new HDIs were added. Review the scan feedback below."
      );
      setScanError("");
    } catch (err) {
      if (queuedItems[0]?.generation !== acquisitionGenerationRef.current) {
        return;
      }
      const updates = {};
      queuedItems.forEach((item) => {
        updates[item.id] = {
          status: "network",
          message: err.message || "Could not validate this code.",
        };
      });
      updateFeedbackItems(updates);
      setScanError(err.message || "Could not validate scanned codes.");
    }
  };

  const enqueueDirectCodes = (rawCodes) => {
    const codes = rawCodes.map((code) => code.trim()).filter(Boolean);
    if (codes.length === 0) {
      setScanError("Enter or scan at least one DataMatrix code.");
      scanInputRef.current?.focus();
      return;
    }
    if (codes.length > DIRECT_CODE_LIMIT) {
      setScanError(`Submit at most ${DIRECT_CODE_LIMIT} codes at a time.`);
      return;
    }

    const queuedItems = codes.map((rawCode) => ({
      id: `scan-${feedbackIdRef.current++}`,
      rawCode,
      code: rawCode,
      status: "pending",
      message: "Validating...",
      generation: acquisitionGenerationRef.current,
    }));
    setDirectFeedback((current) => [...current, ...queuedItems]);
    setScanError("");
    setDownloadError("");
    directQueueRef.current = directQueueRef.current.then(() => processDirectBatch(queuedItems));
  };

  const retryFeedbackItem = (item) => {
    setDirectFeedback((current) => current.filter((candidate) => candidate.id !== item.id));
    enqueueDirectCodes([item.code]);
    window.setTimeout(() => scanInputRef.current?.focus(), 0);
  };

  const removeDirectItem = (index) => {
    const nextGrid = hdiGridRef.current.filter((_box, boxIndex) => boxIndex !== index);
    hdiGridRef.current = nextGrid;
    setHdiGrid(nextGrid);
    setDownloadError("");
    window.setTimeout(() => scanInputRef.current?.focus(), 0);
  };

  const onImageChange = (e) => {
    if (
      hdiGridRef.current.length > 0 &&
      !window.confirm("Selecting a new image will replace the current image-scan results.")
    ) {
      e.target.value = "";
      return;
    }
    setScanError("");
    setScanStatus("");
    hdiGridRef.current = [];
    setHdiGrid([]);
    setDownloadError("");
    setInspectionDateFromExif(false);

    const nextFile = e.target.files && e.target.files[0];
    if (!nextFile) {
      setScanImage(null);
      return;
    }

    const validImageTypes = ["image/jpeg", "image/png", "image/tiff", "image/bmp"];
    if (!validImageTypes.includes(nextFile.type)) {
      setScanError("Please select a valid image file (JPEG, PNG, TIFF, or BMP).");
      setScanImage(null);
      return;
    }

    const maxSize = 20 * 1024 * 1024;
    if (nextFile.size > maxSize) {
      setScanError("File too large. Maximum size is 20MB.");
      setScanImage(null);
      return;
    }

    setScanImage(nextFile);
  };

  const onScanImage = async () => {
    setScanError("");
    setScanStatus("");
    hdiGridRef.current = [];
    setHdiGrid([]);
    setDownloadError("");
    setInspectionDateFromExif(false);

    if (!scanImage) {
      setScanError("No image selected.");
      return;
    }

    if (!auth.isAuthenticated) {
      setScanError("You must be authenticated to scan images.");
      return;
    }

    let timeoutId = null;

    try {
      const form = new FormData();
      form.append("file", scanImage);
      const controller = new AbortController();
      timeoutId = Number.isFinite(SCAN_REQUEST_TIMEOUT_MS)
        ? window.setTimeout(() => controller.abort(), SCAN_REQUEST_TIMEOUT_MS)
        : null;

      setIsScanning(true);
      setScanStatus("Scanning image for DataMatrix codes...");

      const res = await fetch(`${apiUrl}/api/v1/hdi/scan-image`, {
        method: "POST",
        body: form,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${auth.user?.access_token}`,
        },
        credentials: "include",
      });

      if (!res.ok) {
        const errorText = await res.text();
        let errorDetail = errorText;
        try {
          errorDetail = JSON.parse(errorText).detail || errorText;
        } catch (_error) {
          // Keep non-JSON upstream responses as-is.
        }
        throw new Error(`Scan failed: ${res.status} ${errorDetail}`);
      }

      const data = await res.json();
      const exifInspectionDate = data.inspection_date_from_exif;
      const defaultInspectionDate = exifInspectionDate || new Date().toISOString().split("T")[0];
      const sourceGrid = Array.isArray(data.grid_segments)
        ? data.grid_segments
        : data.detected_hdis || [];

      const mappedProductionDates = productionDate
        ? []
        : [
            ...new Set(
              sourceGrid
                .filter((box) => box.production_date_source === "code_rule" && box.production_date)
                .map((box) => box.production_date)
            ),
          ];

      setInspectionDate(defaultInspectionDate);
      setInspectionDateFromExif(Boolean(exifInspectionDate));

      const nextGrid = sourceGrid
        .map((box, index) => normalizeHdiBox(box, index))
        .map((box) =>
          applyMetadataDefaults(box, {
            location,
            componentBatch,
            opticalInspection,
            productionDateOverride: productionDate,
            inspectionDate: defaultInspectionDate,
            comment,
          })
        )
        .sort((a, b) => a.row - b.row || a.column - b.column || a.segment_index - b.segment_index);

      hdiGridRef.current = nextGrid;
      setHdiGrid(nextGrid);

      if (nextGrid.length > 0) {
        const prodDateMessage =
          mappedProductionDates.length === 1
            ? ` Production date ${mappedProductionDates[0]} was detected from the order number.`
            : mappedProductionDates.length > 1
              ? ` Production dates ${mappedProductionDates.join(
                  ", "
                )} were detected from the order numbers.`
              : "";
        const inspectionDateMessage = exifInspectionDate
          ? ` Inspection date ${exifInspectionDate} was extracted from photo EXIF data.`
          : "";
        const processingTimeoutMessage = data.processing_timed_out
          ? " Some segments reached their share of the scan time budget; review empty segments."
          : "";
        const resolvedRows = data.grid_rows || getGridRows(nextGrid);
        const resolvedColumns = data.grid_columns || getGridColumns(nextGrid);

        setScanStatus(
          `Scan complete: found ${
            nextGrid.filter((box) => box.detected).length
          } DataMatrix code(s) across ${
            nextGrid.length
          } segment(s) in a ${resolvedRows} x ${resolvedColumns} layout.${prodDateMessage}${inspectionDateMessage}${processingTimeoutMessage}`
        );
      } else {
        setScanStatus(
          data.processing_timed_out
            ? "Scan reached its processing budget before any PCB segments could be decoded."
            : "Scan complete, but no PCB segments could be derived from the image."
        );
      }
    } catch (err) {
      if (err?.name === "AbortError") {
        const timeoutSeconds = Math.round(SCAN_REQUEST_TIMEOUT_MS / 1000);
        setScanError(
          `Scan timed out after ${timeoutSeconds} seconds. The backend is still likely processing too slowly for the current timeout.`
        );
      } else if (err instanceof TypeError) {
        setScanError(
          "Network error while scanning image. This often means an upstream timeout (route/proxy/backend) closed the connection."
        );
      } else {
        setScanError(err.message || "Scan failed");
      }
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setIsScanning(false);
    }
  };

  const updateHdiField = (index, field, value) => {
    setDownloadError("");
    setHdiGrid((currentGrid) => {
      const nextGrid = currentGrid.map((box, boxIndex) =>
        boxIndex === index
          ? {
              ...box,
              [field]: value,
              ...(field === "production_date" ? { production_date_source: "" } : {}),
            }
          : box
      );
      hdiGridRef.current = nextGrid;
      return nextGrid;
    });
  };

  const toggleBoxEnabled = (index) => {
    setDownloadError("");
    setHdiGrid((currentGrid) => {
      const nextGrid = currentGrid.map((box, boxIndex) =>
        boxIndex === index
          ? {
              ...box,
              enabled: !box.enabled,
            }
          : box
      );
      hdiGridRef.current = nextGrid;
      return nextGrid;
    });
  };

  const applyMetadataToAll = () => {
    if (hdiGrid.length === 0) {
      setScanError("No HDI items to update.");
      return;
    }

    setDownloadError("");
    const productionDateToApply = productionDate || commonMappedProductionDate;
    setHdiGrid((currentGrid) => {
      const nextGrid = currentGrid.map((box) => ({
        ...box,
        location,
        component_mounting_batch: componentBatch,
        optical_inspection: opticalInspection,
        production_date: productionDateToApply || box.production_date,
        production_date_source: productionDate ? "" : box.production_date_source,
        inspection_date: inspectionDate,
        comments: comment,
      }));
      hdiGridRef.current = nextGrid;
      return nextGrid;
    });
    setScanStatus("Metadata applied to all HDI items.");
  };

  const clearSession = () => {
    if (window.confirm("Clear all scanned HDI data and reset the form? This cannot be undone.")) {
      acquisitionGenerationRef.current += 1;
      directQueueRef.current = Promise.resolve();
      setScanImage(null);
      hdiGridRef.current = [];
      setHdiGrid([]);
      setDirectInput("");
      setBulkInput("");
      setDirectFeedback([]);
      setScanStatus("");
      setScanError("");
      setLocation("PSI");
      setComponentBatch("");
      setOpticalInspection("");
      setProductionDate("");
      setInspectionDate(new Date().toISOString().split("T")[0]);
      setComment("");
      setInspectionDateFromExif(false);
      setDownloadError("");
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setScanStatus("Session cleared.");
    }
  };

  const downloadHdisJson = () => {
    setDownloadError("");

    if (enabledExportHdis.length === 0) {
      setDownloadError("No enabled HDI items are available to download.");
      return;
    }

    const invalidBoxes = hdiGrid
      .map((box, index) => ({
        box,
        index,
        missingFields: REQUIRED_EXPORT_FIELDS.filter(([field]) => {
          if (!box.enabled) {
            return false;
          }
          const serialized = serializeHdiBox(box);
          return !serialized[field];
        }).map(([, label]) => label),
      }))
      .filter((entry) => entry.box.enabled && entry.missingFields.length > 0);

    if (invalidBoxes.length > 0) {
      const details = invalidBoxes
        .map(({ box, index, missingFields }) => {
          const position =
            box.source === "direct"
              ? `item ${index + 1}`
              : `row ${box.row + 1}, column ${box.column + 1}`;
          return `${getBoxTitle(box, index)} (${position}): ${missingFields.join(", ")}`;
        })
        .join(" | ");
      setDownloadError(
        `Please fill all required fields for enabled items before downloading. ${details}`
      );
      return;
    }

    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `hdis_${timestamp}.json`;

    const jsonString = JSON.stringify(enabledExportHdis, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 20, maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <h1>HDI Inspection</h1>
        <Link to="/help#hdi-inspection">Help for HDI Inspection</Link>
      </div>

      {!auth.isAuthenticated ? (
        <>
          <p>You must be logged in to scan and inspect HDIs.</p>
          <button
            onClick={() => {
              const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
              sessionStorage.setItem("post_login_redirect", returnTo);
              auth.signinRedirect({ state: { returnTo } });
            }}
          >
            Log in
          </button>
        </>
      ) : (
        <>
          <section
            style={{ marginBottom: 40, padding: 20, border: "1px solid #ddd", borderRadius: 8 }}
          >
            <h2>Step 1: Add HDIs</h2>
            <p>Choose one input method for this inspection session.</p>
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              <button
                type="button"
                aria-pressed={isDirectMode}
                onClick={() => switchAcquisitionMode("direct")}
                style={{
                  padding: "10px 16px",
                  borderRadius: 4,
                  border: "1px solid #007bff",
                  background: isDirectMode ? "#007bff" : "white",
                  color: isDirectMode ? "white" : "#0056b3",
                }}
              >
                Direct entry
              </button>
              <button
                type="button"
                aria-pressed={!isDirectMode}
                onClick={() => switchAcquisitionMode("image")}
                style={{
                  padding: "10px 16px",
                  borderRadius: 4,
                  border: "1px solid #007bff",
                  background: !isDirectMode ? "#007bff" : "white",
                  color: !isDirectMode ? "white" : "#0056b3",
                }}
              >
                Image upload
              </button>
            </div>

            {isDirectMode ? (
              <>
                <p>
                  Scan one DataMatrix code at a time. The scanner&apos;s Enter/CR suffix submits the
                  code and leaves this field ready for the next scan.
                </p>
                <label htmlFor="direct-hdi-code" style={{ display: "block", marginBottom: 8 }}>
                  <strong>Handscanner or single code</strong>
                </label>
                <input
                  id="direct-hdi-code"
                  ref={scanInputRef}
                  type="text"
                  value={directInput}
                  onChange={(e) => setDirectInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const value = directInput;
                      setDirectInput("");
                      enqueueDirectCodes([value]);
                    }
                  }}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Scan a code beginning with GS, then press Enter"
                  style={{
                    width: "100%",
                    maxWidth: 720,
                    padding: 12,
                    borderRadius: 4,
                    border: "2px solid #007bff",
                    fontFamily: "monospace",
                    fontSize: 16,
                    marginBottom: 20,
                  }}
                />

                <label htmlFor="bulk-hdi-codes" style={{ display: "block", marginBottom: 8 }}>
                  <strong>Bulk paste</strong> (one code per line, maximum {DIRECT_CODE_LIMIT})
                </label>
                <textarea
                  id="bulk-hdi-codes"
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  rows={6}
                  spellCheck={false}
                  placeholder={"GS1084373015021\nGS1084590011027"}
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: 720,
                    padding: 10,
                    borderRadius: 4,
                    border: "1px solid #999",
                    fontFamily: "monospace",
                    marginBottom: 10,
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const codes = bulkInput.split(/\r?\n/);
                    if (codes.filter((code) => code.trim()).length <= DIRECT_CODE_LIMIT) {
                      setBulkInput("");
                    }
                    enqueueDirectCodes(codes);
                    window.setTimeout(() => scanInputRef.current?.focus(), 0);
                  }}
                  disabled={!bulkInput.trim()}
                  style={{ padding: "10px 18px", marginRight: 10 }}
                >
                  Add bulk codes
                </button>
                <Link to="/help#accepted-hdi-codes">View accepted code formats</Link>

                {directFeedback.length > 0 && (
                  <div
                    aria-live="polite"
                    aria-label="Scanner feedback"
                    style={{ marginTop: 20, maxWidth: 900 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3 style={{ marginBottom: 8 }}>Scan feedback</h3>
                      <button type="button" onClick={() => setDirectFeedback([])}>
                        Clear feedback
                      </button>
                    </div>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {directFeedback.map((item) => {
                        const color =
                          item.status === "accepted"
                            ? "#2e7d32"
                            : item.status === "pending"
                              ? "#555"
                              : "#b42318";
                        return (
                          <li
                            key={item.id}
                            style={{
                              display: "flex",
                              gap: 10,
                              justifyContent: "space-between",
                              padding: "8px 10px",
                              marginBottom: 6,
                              border: "1px solid #ddd",
                              borderRadius: 4,
                              color,
                            }}
                          >
                            <span>
                              <code>{item.code}</code> — {item.message}
                            </span>
                            <span style={{ whiteSpace: "nowrap" }}>
                              {item.status === "network" && (
                                <button
                                  type="button"
                                  onClick={() => retryFeedbackItem(item)}
                                  style={{ marginRight: 6 }}
                                >
                                  Retry
                                </button>
                              )}
                              {item.status === "invalid" && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDirectInput(item.code);
                                    setDirectFeedback((current) =>
                                      current.filter((candidate) => candidate.id !== item.id)
                                    );
                                    window.setTimeout(() => scanInputRef.current?.focus(), 0);
                                  }}
                                  style={{ marginRight: 6 }}
                                >
                                  Edit and retry
                                </button>
                              )}
                              {item.status !== "pending" && (
                                <button
                                  type="button"
                                  aria-label={`Dismiss feedback for ${item.code}`}
                                  onClick={() =>
                                    setDirectFeedback((current) =>
                                      current.filter((candidate) => candidate.id !== item.id)
                                    )
                                  }
                                >
                                  Dismiss
                                </button>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <p>
                  Upload an image of the HDI panel. The scanner preserves panel order and creates a
                  placeholder for segments where decoding or validation fails.
                </p>
                <div style={{ marginBottom: 15 }}>
                  <label htmlFor="hdi-panel-image" style={{ display: "block", marginBottom: 10 }}>
                    <strong>Image File (max 20MB):</strong>
                  </label>
                  <input
                    id="hdi-panel-image"
                    type="file"
                    accept="image/jpeg,image/png,image/tiff,image/bmp,.jpg,.jpeg,.png,.tiff,.tif,.bmp"
                    onChange={onImageChange}
                    disabled={isScanning}
                  />
                </div>
                <button
                  onClick={onScanImage}
                  disabled={!scanImage || isScanning}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: isScanning ? "#ccc" : "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: isScanning ? "not-allowed" : "pointer",
                    marginRight: 10,
                  }}
                >
                  {isScanning ? "Scanning..." : "Scan Image for DataMatrix Codes"}
                </button>
              </>
            )}

            {scanError && (
              <p role="alert" style={{ color: "red", marginBottom: 10 }}>
                {scanError}
              </p>
            )}

            {hdiGrid.length > 0 && (
              <button
                onClick={clearSession}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: "14px",
                  marginLeft: 10,
                }}
              >
                Clear Session
              </button>
            )}

            {scanStatus && (
              <p
                role="status"
                style={{
                  marginTop: 10,
                  padding: 10,
                  backgroundColor: "#e8f5e9",
                  borderRadius: 4,
                  color: "#2e7d32",
                }}
              >
                {scanStatus}
              </p>
            )}
          </section>

          <section
            style={{ marginBottom: 40, padding: 20, border: "1px solid #ddd", borderRadius: 8 }}
          >
            <h2>Step 2: Inspection Metadata</h2>
            <p>
              Configure common inspection details. Apply them to every HDI and then fine-tune
              individual entries below.
            </p>

            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 15, marginBottom: 15 }}
            >
              <div>
                <label style={{ display: "block", marginBottom: 5 }}>
                  <strong>Location:</strong>
                </label>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #999" }}
                >
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 5 }}>
                  <strong>Component Mounting Batch:</strong>
                </label>
                {allTepx ? (
                  <select
                    value={componentBatch}
                    onChange={(e) => setComponentBatch(e.target.value)}
                    style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #999" }}
                  >
                    <option value="">Select batch...</option>
                    {COMPONENT_BATCH_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <input
                      type="text"
                      list="batch-options-global"
                      value={componentBatch}
                      onChange={(e) => setComponentBatch(e.target.value)}
                      placeholder="e.g. Elfab December 2024"
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 4,
                        border: "1px solid #999",
                      }}
                    />
                    <datalist id="batch-options-global">
                      {COMPONENT_BATCH_OPTIONS.map((option) => (
                        <option key={option} value={option} />
                      ))}
                    </datalist>
                  </>
                )}
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 5 }}>
                  <strong>Optical Inspection:</strong>
                </label>
                <select
                  value={opticalInspection}
                  onChange={(e) => setOpticalInspection(e.target.value)}
                  style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #999" }}
                >
                  <option value="">Select result...</option>
                  {opticalInspectionOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 5 }}>
                  <strong>Production Date:</strong>
                </label>
                <input
                  type="date"
                  aria-label="Default production date"
                  value={effectiveProductionDate}
                  onChange={(e) => setProductionDate(e.target.value)}
                  style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #999" }}
                />
                {commonMappedProductionDate && !productionDate ? (
                  <p
                    style={{
                      margin: "5px 0 0 0",
                      fontSize: "12px",
                      color: "#1976d2",
                      fontStyle: "italic",
                    }}
                  >
                    Auto-detected from the HDI order mapping and ready to apply to all items.
                  </p>
                ) : (
                  hasMappedProductionDate && (
                    <p
                      style={{
                        margin: "5px 0 0 0",
                        fontSize: "12px",
                        color: "#1976d2",
                        fontStyle: "italic",
                      }}
                    >
                      Auto-detected production dates stay in place unless you override them here.
                    </p>
                  )
                )}
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 5 }}>
                  <strong>Inspection Date:</strong>
                </label>
                <input
                  type="date"
                  value={inspectionDate}
                  onChange={(e) => {
                    setInspectionDate(e.target.value);
                    setInspectionDateFromExif(false);
                  }}
                  style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #999" }}
                />
                {inspectionDateFromExif && (
                  <p
                    style={{
                      margin: "5px 0 0 0",
                      fontSize: "12px",
                      color: "#1976d2",
                      fontStyle: "italic",
                    }}
                  >
                    Inspection date was extracted from the photo EXIF metadata.
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 15 }}>
              <label style={{ display: "block", marginBottom: 5 }}>
                <strong>Comments:</strong>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional comments..."
                rows={3}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #999",
                  fontFamily: "Arial",
                }}
              />
            </div>

            {hdiGrid.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <button
                  onClick={applyMetadataToAll}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#ff9800",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Apply Metadata to All {hdiGrid.length} Item(s)
                </button>
                <p style={{ marginTop: 10, fontSize: "13px", color: "#666" }}>
                  Disabled items stay excluded from the JSON export until you enable them again.
                </p>
              </div>
            )}
          </section>

          {hdiGrid.length > 0 && (
            <section
              style={{
                marginBottom: 40,
                padding: 20,
                border: "2px solid #4caf50",
                borderRadius: 8,
              }}
            >
              <h2>Step 3: {isDirectMode ? "Review HDIs" : "HDI Grid"}</h2>
              {isDirectMode ? (
                <p>
                  Review HDIs in scan order. Parsed identification is locked; grading and inspection
                  metadata remain editable.
                </p>
              ) : (
                <p>
                  Review each segment in panel order. Enable placeholders to enter missing HDIs
                  manually, or disable a detected item to exclude it from the JSON.
                </p>
              )}
              <p style={{ color: "#555", marginTop: 0 }}>
                {isDirectMode
                  ? `Scanned: ${hdiGrid.length}. Included in export: ${enabledCount}.`
                  : `Layout: ${gridRows} row(s) x ${gridColumns} column(s). Detected: ${detectedCount}. Included in export: ${enabledCount}.`}
              </p>

              <div style={{ overflowX: "auto" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isDirectMode
                      ? "minmax(0, 1fr)"
                      : `repeat(${gridColumns}, minmax(320px, 1fr))`,
                    gap: 16,
                    minWidth: isDirectMode ? 0 : `${Math.max(gridColumns, 1) * 320}px`,
                  }}
                >
                  {hdiGrid.map((box, index) => {
                    const inputsDisabled = !box.enabled;
                    const scannedFieldsReadOnly = box.detected;
                    const borderColor = inputsDisabled
                      ? "#bdbdbd"
                      : box.detected
                        ? "#4caf50"
                        : "#ff9800";
                    const backgroundColor = inputsDisabled
                      ? "#f5f5f5"
                      : box.detected
                        ? "#f9fff8"
                        : "#fff8e1";

                    return (
                      <div
                        key={`${box.source}-${box.row}-${box.column}-${box.segment_index}`}
                        style={{
                          padding: 15,
                          border: `2px solid ${borderColor}`,
                          borderRadius: 8,
                          backgroundColor,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "flex-start",
                            marginBottom: 12,
                          }}
                        >
                          <div>
                            <h3 style={{ margin: "0 0 4px 0" }}>
                              {box.source === "direct"
                                ? `Item ${index + 1}: ${getBoxTitle(box, index)}`
                                : `Row ${box.row + 1}, Column ${box.column + 1}`}
                            </h3>
                            <p style={{ margin: 0, color: "#555", fontSize: "14px" }}>
                              {box.detected
                                ? box.source === "direct"
                                  ? `Entered directly from ${box.data_matrix}`
                                  : `Detected from image: ${getBoxTitle(box, index)}`
                                : box.validation_error
                                  ? `A code was decoded but rejected: ${box.validation_error.message}`
                                  : box.scan_timed_out
                                    ? "The scan time budget was exhausted for this segment."
                                    : "No DataMatrix code detected for this segment."}
                            </p>
                          </div>
                          <div style={{ display: "grid", gap: 8, justifyItems: "end" }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="checkbox"
                                checked={box.enabled}
                                onChange={() => toggleBoxEnabled(index)}
                              />
                              Include in JSON
                            </label>
                            {box.source === "direct" && (
                              <button type="button" onClick={() => removeDirectItem(index)}>
                                Remove
                              </button>
                            )}
                          </div>
                        </div>

                        {!box.detected && !box.enabled && (
                          <p style={{ marginTop: 0, color: "#666", fontSize: "13px" }}>
                            {box.validation_error ? (
                              <>
                                Correct the code using direct-entry mode or update the configured
                                rules. <Link to="/help#accepted-hdi-codes">Accepted formats</Link>
                              </>
                            ) : (
                              "Enable this placeholder if you want to enter the missing HDI manually."
                            )}
                          </p>
                        )}

                        {box.detected && (
                          <p style={{ marginTop: 0, color: "#666", fontSize: "13px" }}>
                            Scan-derived identification fields are locked for detected HDIs.
                          </p>
                        )}

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          {IDENTIFICATION_FIELDS.map(([field, label]) => (
                            <div key={field}>
                              <label style={{ display: "block", marginBottom: 5 }}>
                                <strong>{label}:</strong>
                              </label>
                              <input
                                type="text"
                                value={box[field] || ""}
                                onChange={(e) => updateHdiField(index, field, e.target.value)}
                                disabled={inputsDisabled || scannedFieldsReadOnly}
                                style={{
                                  width: "100%",
                                  padding: 6,
                                  borderRadius: 4,
                                  border: "1px solid #999",
                                  fontFamily: field === "data_matrix" ? "monospace" : "inherit",
                                  backgroundColor:
                                    inputsDisabled || scannedFieldsReadOnly ? "#eee" : "white",
                                }}
                              />
                            </div>
                          ))}
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 10,
                          }}
                        >
                          <div>
                            <label style={{ display: "block", marginBottom: 5 }}>
                              <strong>Location:</strong>
                            </label>
                            <select
                              value={box.location}
                              onChange={(e) => updateHdiField(index, "location", e.target.value)}
                              disabled={inputsDisabled}
                              style={{
                                width: "100%",
                                padding: 6,
                                borderRadius: 4,
                                border: "1px solid #999",
                                backgroundColor: inputsDisabled ? "#eee" : "white",
                              }}
                            >
                              {locations.map((loc) => (
                                <option key={loc} value={loc}>
                                  {loc}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label style={{ display: "block", marginBottom: 5 }}>
                              <strong>Component Mounting Batch:</strong>
                            </label>
                            {box.detector === "TEPX" ? (
                              <select
                                value={box.component_mounting_batch}
                                onChange={(e) =>
                                  updateHdiField(index, "component_mounting_batch", e.target.value)
                                }
                                disabled={inputsDisabled}
                                style={{
                                  width: "100%",
                                  padding: 6,
                                  borderRadius: 4,
                                  border: "1px solid #999",
                                  backgroundColor: inputsDisabled ? "#eee" : "white",
                                }}
                              >
                                <option value="">Select batch...</option>
                                {COMPONENT_BATCH_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <>
                                <input
                                  type="text"
                                  list={`batch-options-${index}`}
                                  value={box.component_mounting_batch}
                                  onChange={(e) =>
                                    updateHdiField(
                                      index,
                                      "component_mounting_batch",
                                      e.target.value
                                    )
                                  }
                                  disabled={inputsDisabled}
                                  style={{
                                    width: "100%",
                                    padding: 6,
                                    borderRadius: 4,
                                    border: "1px solid #999",
                                    backgroundColor: inputsDisabled ? "#eee" : "white",
                                  }}
                                />
                                <datalist id={`batch-options-${index}`}>
                                  {COMPONENT_BATCH_OPTIONS.map((option) => (
                                    <option key={option} value={option} />
                                  ))}
                                </datalist>
                              </>
                            )}
                          </div>

                          <div>
                            <label style={{ display: "block", marginBottom: 5 }}>
                              <strong>Optical Inspection:</strong>
                            </label>
                            <select
                              value={box.optical_inspection}
                              onChange={(e) =>
                                updateHdiField(index, "optical_inspection", e.target.value)
                              }
                              disabled={inputsDisabled}
                              style={{
                                width: "100%",
                                padding: 6,
                                borderRadius: 4,
                                border: "1px solid #999",
                                backgroundColor: inputsDisabled ? "#eee" : "white",
                              }}
                            >
                              <option value="">Select result...</option>
                              {opticalInspectionOptions.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label style={{ display: "block", marginBottom: 5 }}>
                              <strong>Production Date:</strong>
                            </label>
                            <input
                              type="date"
                              aria-label={`Production date for ${getBoxTitle(box, index)}`}
                              value={box.production_date || ""}
                              onChange={(e) =>
                                updateHdiField(index, "production_date", e.target.value)
                              }
                              disabled={inputsDisabled}
                              style={{
                                width: "100%",
                                padding: 6,
                                borderRadius: 4,
                                border: "1px solid #999",
                                backgroundColor: inputsDisabled ? "#eee" : "white",
                              }}
                            />
                            {box.production_date_source === "code_rule" && (
                              <p
                                style={{
                                  margin: "5px 0 0 0",
                                  fontSize: "11px",
                                  color: "#1976d2",
                                  fontStyle: "italic",
                                }}
                              >
                                Auto-detected from order {box.order_number}
                              </p>
                            )}
                          </div>

                          <div>
                            <label style={{ display: "block", marginBottom: 5 }}>
                              <strong>Inspection Date:</strong>
                            </label>
                            <input
                              type="date"
                              value={box.inspection_date || ""}
                              onChange={(e) =>
                                updateHdiField(index, "inspection_date", e.target.value)
                              }
                              disabled={inputsDisabled}
                              style={{
                                width: "100%",
                                padding: 6,
                                borderRadius: 4,
                                border: "1px solid #999",
                                backgroundColor: inputsDisabled ? "#eee" : "white",
                              }}
                            />
                          </div>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <label style={{ display: "block", marginBottom: 5 }}>
                            <strong>Comments:</strong>
                          </label>
                          <textarea
                            value={box.comments}
                            onChange={(e) => updateHdiField(index, "comments", e.target.value)}
                            rows={2}
                            disabled={inputsDisabled}
                            style={{
                              width: "100%",
                              padding: 6,
                              borderRadius: 4,
                              border: "1px solid #999",
                              fontFamily: "Arial",
                              backgroundColor: inputsDisabled ? "#eee" : "white",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {downloadError && (
                <p
                  style={{
                    color: "#d32f2f",
                    backgroundColor: "#ffebee",
                    padding: 15,
                    borderRadius: 4,
                    marginTop: 20,
                    marginBottom: 15,
                    border: "1px solid #ef5350",
                  }}
                >
                  {downloadError}
                </p>
              )}

              <button
                onClick={downloadHdisJson}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#4caf50",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginTop: 10,
                }}
              >
                Download hdis.json ({enabledCount})
              </button>
            </section>
          )}
        </>
      )}
    </div>
  );
}
